import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../App';
import { get } from '../data/api';
import {
  KENYA_CONNECT_CATEGORIES,
  KENYA_CONNECT_GUIDES,
  INSURANCE_PARTNERS,
  POPULAR_KENYA_COUNTIES,
} from '../data/kenya_connect';
import { KenyaListingSubmitModal, FeaturedRequestModal } from './CommunitySubmit';
import './Community.css';
import './KenyaConnect.css';

function ListingCard({ item, onFeature, canFeature }) {
  const featured = item.listing_tier === 'featured';
  return (
    <div className={`kc-card ${featured ? 'featured' : ''}`}>
      {item.image_url && (
        <img src={item.image_url} alt="" className="kc-card-img" />
      )}
      <div className="kc-card-body">
        <div className="kc-card-title">{item.title}</div>
        <div className="kc-card-meta">
          {item.type} · 📍 {item.kenya_county}{item.kenya_area ? `, ${item.kenya_area}` : ''}
        </div>
        {item.price && <div className="kc-card-price">{item.price_currency} {item.price}</div>}
        {item.size_info && <div className="text-sm text-muted">{item.size_info}</div>}
        <p className="text-sm" style={{ marginTop: 8 }}>{item.description?.slice(0, 100)}…</p>
        <div className="comm-badges" style={{ marginTop: 8 }}>
          {featured && <span className="comm-badge featured">Featured</span>}
        </div>
        {(item.contact_phone || item.contact_email) && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {item.contact_phone && (
              <a href={`tel:${item.contact_phone}`} className="btn btn-outline btn-sm">Call</a>
            )}
            {item.contact_email && (
              <a href={`mailto:${item.contact_email}`} className="btn btn-outline btn-sm">Email</a>
            )}
          </div>
        )}
        {canFeature && onFeature && (
          <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => onFeature(item)}>
            ⭐ Feature this listing
          </button>
        )}
      </div>
    </div>
  );
}

export default function KenyaConnect() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { token, user } = useApp();

  const [activeType, setActiveType] = useState(params.get('type') || 'land');
  const [county, setCounty]         = useState(params.get('county') || '');
  const [listings, setListings]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showSubmit, setShowSubmit] = useState(false);
  const [featureTarget, setFeatureTarget] = useState(null);

  const category = KENYA_CONNECT_CATEGORIES.find(c => c.id === activeType) || KENYA_CONNECT_CATEGORIES[0];
  const guide    = KENYA_CONNECT_GUIDES[activeType];

  useEffect(() => {
    setParams({ type: activeType, ...(county ? { county } : {}) }, { replace: true });
  }, [activeType, county, setParams]);

  useEffect(() => {
    setLoading(true);
    get('kenya_listings', { action: 'list', type: activeType, ...(county ? { county } : {}) })
      .then(d => setListings(d.listings || []))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [activeType, county]);

  return (
    <div className="kc-page">
      <nav className="comm-nav">
        <div className="container comm-nav-inner">
          <Link to="/" className="comm-logo">mudiaspora<span style={{ color: 'var(--text-muted)' }}>.</span></Link>
          <div className="comm-nav-links">
            <Link to="/community">Japan Community</Link>
            <Link to="/japan-life">Japan Life</Link>
            <Link to="/kenya-connect">Kenya Connect</Link>
            {token ? <Link to="/dashboard">Dashboard</Link> : <Link to="/login">Sign In</Link>}
          </div>
        </div>
      </nav>

      <section className="kc-hero">
        <div className="container">
          <h1>Kenya <span>Connect</span></h1>
          <p>Land, livestock, investments, and insurance — managed from anywhere in the diaspora.</p>
        </div>
      </section>

      <div className="container kc-layout">
        <aside className="kc-sidebar">
          {KENYA_CONNECT_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              type="button"
              className={`kc-cat-btn ${activeType === cat.id ? 'active' : ''}`}
              onClick={() => setActiveType(cat.id)}
            >
              <span>{cat.icon}</span> {cat.name}
            </button>
          ))}
          <hr style={{ margin: '12px 0', border: 'none', borderTop: '1px solid var(--border)' }} />
          <div className="comm-field">
            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>County filter</label>
            <select value={county} onChange={e => setCounty(e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid var(--border)' }}>
              <option value="">All counties</option>
              {POPULAR_KENYA_COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Link to="/dashboard" className="btn btn-outline btn-sm" style={{ width: '100%', marginTop: 12, textAlign: 'center' }}>
            Kenya dashboard →
          </Link>
        </aside>

        <main>
          <div className="kc-intro">
            <h2>{category.icon} {category.name}</h2>
            <p className="text-muted">{category.desc}</p>
          </div>

          {guide && (
            <div className="kc-guide">
              <h3>{guide.title}</h3>
              <ol>{guide.steps.map((step, i) => <li key={i}>{step}</li>)}</ol>
              {guide.tips?.length > 0 && (
                <div className="kc-tips"><strong>Tips:</strong> {guide.tips.join(' ')}</div>
              )}
            </div>
          )}

          {activeType === 'insurance' && (
            <div className="kc-partners">
              <h3>Partner insurers</h3>
              <div className="kc-partner-grid">
                {INSURANCE_PARTNERS.map(p => (
                  <div key={p.name} className="kc-partner-card">
                    <strong>{p.name}</strong>
                    <p className="text-sm text-muted">{p.note}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted" style={{ marginTop: 12 }}>
                Contact admin@mudiaspora.com for introductions to diaspora desks.
              </p>
            </div>
          )}

          <div className="kc-list-header">
            <h3>Listings{county ? ` in ${county}` : ''}</h3>
            {token ? (
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowSubmit(true)}>
                + List {category.name.toLowerCase()}
              </button>
            ) : (
              <button type="button" className="btn btn-outline btn-sm" onClick={() => navigate('/login')}>
                Sign in to list
              </button>
            )}
          </div>

          {loading && <p className="text-muted">Loading…</p>}
          {!loading && listings.length === 0 && (
            <p className="text-muted">No listings yet. Be the first to post one.</p>
          )}
          <div className="kc-grid">
            {listings.map(item => (
              <ListingCard
                key={item.id}
                item={item}
                canFeature={token && String(item.owner_id) === String(user?.user_id || user?.id)}
                onFeature={setFeatureTarget}
              />
            ))}
          </div>
        </main>
      </div>

      {showSubmit && (
        <KenyaListingSubmitModal
          type={activeType}
          onClose={() => setShowSubmit(false)}
          onDone={() => {
            get('kenya_listings', { action: 'list', type: activeType, ...(county ? { county } : {}) })
              .then(d => setListings(d.listings || []));
          }}
        />
      )}

      {featureTarget && (
        <FeaturedRequestModal
          entityType="kenya_listing"
          entityId={featureTarget.id}
          entityLabel={featureTarget.title}
          onClose={() => setFeatureTarget(null)}
        />
      )}
    </div>
  );
}
